/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
import videojs from "!video.js";

import { ClientSideAdvertisement, PlayerReferenceBase } from "../../types";
import { AdTracker } from "../CurrentAdTracker";
import { EventManagerVideoJS } from "../EventManager";
import { PlayerTrackerVideoJS } from "../PlayerTracker";

// transpiling issue with video.js, !syntax with disabled webpack loader required
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved

type GoogleIMA = typeof google.ima;

// eslint-disable-next-line @typescript-eslint/no-var-requires
videojs.registerPlugin("ads", require("videojs-contrib-ads").default);

export class ClientSideAdvertisementVideoJS implements ClientSideAdvertisement {
  private ima: GoogleIMA;
  private readonly player: videojs.Player;
  private readonly refs: PlayerReferenceBase;

  private playerTracker: PlayerTrackerVideoJS;
  private eventManager: EventManagerVideoJS | undefined;

  private displayContainer: google.ima.AdDisplayContainer | undefined;
  private loader: google.ima.AdsLoader | undefined;

  private startMuted = false;
  private playerInErrorState = false;

  constructor(
    ima: GoogleIMA,
    player: videojs.Player | shaka.Player,
    refs: PlayerReferenceBase,
  ) {
    this.ima = ima;
    this.player = player;
    this.refs = refs;
    this.playerTracker = new PlayerTrackerVideoJS(
      this.player,
      this.onPlayerResize,
    );

    // initialize videojs ads plugin
    this.player.ads();

    this.player.on("adlinearmodeend", () => {
      window.setTimeout(() => {
        !this.player.ended() && this.player.play();
      }, 100);
    });
  }

  initialize(tagUrl: string, startMuted: boolean) {
    this.startMuted = startMuted;

    const { videoElement, imaContainerElement } = this.refs;

    this.dispose();
    this.playerTracker.initialize();

    this.displayContainer = new google.ima.AdDisplayContainer(
      imaContainerElement,
      videoElement,
    );

    const loader = (this.loader = new google.ima.AdsLoader(
      this.displayContainer,
    ));

    loader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onManagerLoaded,
      false,
    );

    loader.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onLoadedError,
      false,
    );

    this.player.on("readyforpreroll", () => {
      this.eventManager?.initialize(this.playerTracker.dimension, () => {
        this.displayContainer?.initialize();
      });
    });

    this.player.on("contentended", () => {
      loader.contentComplete();

      this.eventManager
        ? this.eventManager.contentComplete()
        : this.player.trigger("nopostroll");
    });

    loader.requestAds(this.prepareAdsRequest(tagUrl));
  }

  dispose() {
    this.playerTracker.dispose();
    this.eventManager?.dispose();
  }

  onPlayerError() {
    this.playerInErrorState = true;
    this.dispose();
  }

  onPlayerReload() {
    this.playerInErrorState = false;
  }

  get tracker(): AdTracker {
    if (!this.eventManager) {
      throw new Error("Advertisement event manager is not initialized");
    }

    return this.eventManager.adTracker;
  }

  get cuePoints(): number[] {
    return this.eventManager?.cuePoints || [];
  }

  volume(value: number) {
    this.eventManager?.volume(value);
  }

  muted(state: boolean) {
    this.eventManager?.muted(state);
  }

  pause() {
    this.eventManager?.pause();
  }

  resume() {
    this.eventManager?.resume();
  }

  skip() {
    this.eventManager?.skip();
  }

  private prepareAdsRequest(tagUrl: string) {
    const { width, height } = this.playerTracker.dimension;

    const adsRequest = new google.ima.AdsRequest();

    adsRequest.adTagUrl = tagUrl;
    adsRequest.linearAdSlotWidth = width;
    adsRequest.linearAdSlotHeight = height;

    adsRequest.setAdWillAutoPlay(true);

    return adsRequest;
  }

  private onManagerLoaded = (event: google.ima.AdsManagerLoadedEvent) => {
    const manager = event.getAdsManager(
      this.playerTracker.playhead,
      this.renderingSettings(),
    );

    this.eventManager?.dispose();
    this.eventManager = new EventManagerVideoJS(
      manager,
      this.player,
      this.startMuted,
    );

    if (this.playerInErrorState) {
      manager.discardAdBreak();
      manager.destroy();
    } else {
      this.player.trigger("adsready");
    }
  };

  private onLoadedError = (event: google.ima.AdErrorEvent) => {
    console.error("Unable to load advertisement system:", event.getError());

    this.player.trigger("nopreroll");
    this.player.trigger("nopostroll");

    this.eventManager?.dispose();
  };

  private onPlayerResize = (width: number, height: number) => {
    this.eventManager?.onPlayerResize(width, height);
  };

  onEnterFullScreen(): void {
    this.eventManager?.onEnterFullScreen();
  }

  onLeaveFullScreen(): void {
    const width = this.playerTracker.getPlayerWidth();
    const height = this.playerTracker.getPlayerHeight();

    this.eventManager?.onLeaveFullScreen(width, height);
  }

  private renderingSettings() {
    const settings = new google.ima.AdsRenderingSettings();
    settings.restoreCustomPlaybackStateOnAdBreakComplete = true;

    return settings;
  }
}
